import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.map((c) => c.toLowerCase()).includes(discountCodeValue.toLowerCase())) {
      discountCode.value = '';
      return;
    }

    cartDiscountError.classList.add('hidden');
    cartDiscountError.classList.remove('cart-discount__error--shipping-notice');
    cartDiscountError.classList.remove('cart-discount__error--invalid');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        let errorMsg = 'Failed to apply discount. Please try again.';
        try {
          const errData = await response.json();
          if (errData.description) errorMsg = errData.description;
          else if (errData.message) errorMsg = errData.message;
        } catch (_) {}
        throw new Error(errorMsg);
      }

      const data = await response.json();

      // Check if this discount code was explicitly marked non-applicable (invalid code or minimum reqs not met)
      const matchedDiscount = Array.isArray(data.discount_codes)
        ? data.discount_codes.find((d) => d.code?.toLowerCase() === discountCodeValue.toLowerCase())
        : null;

      if (matchedDiscount && matchedDiscount.applicable === false) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      // Check if discount was applied to items or cart subtotal
      const hasItemDiscount =
        (typeof data.total_discount === 'number' && data.total_discount > 0) ||
        (Array.isArray(data.items) &&
          data.items.some(
            (item) =>
              (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) ||
              (item.final_line_price && item.original_line_price && item.final_line_price < item.original_line_price) ||
              (item.final_price && item.original_price && item.final_price < item.original_price)
          )) ||
        (Array.isArray(data.cart_level_discount_applications) && data.cart_level_discount_applications.length > 0);

      const newHtml = data.sections?.[this.dataset.sectionId];

      if (hasItemDiscount) {
        // Successful item / cart discount!
        discountCode.value = '';
        cartDiscountError.classList.add('hidden');
        cartDiscountErrorDiscountCode.classList.add('hidden');
        cartDiscountErrorShipping.classList.add('hidden');

        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });

        if (newHtml) {
          morphSection(this.dataset.sectionId, newHtml, {
            mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          });
        }
        return;
      }

      // If not an item discount, check if this is an accepted shipping discount code
      const isShippingDiscount = matchedDiscount && matchedDiscount.applicable === true;
      if (isShippingDiscount) {
        discountCode.value = '';
        this.#handleDiscountError('shipping');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        if (newHtml) {
          morphSection(this.dataset.sectionId, newHtml, {
            mode: this.closest('theme-drawer') ? 'hydration' : 'full',
          });
        }
        return;
      }

      // Default fallback
      discountCode.value = '';
      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      if (newHtml) {
        morphSection(this.dataset.sectionId, newHtml, {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event instanceof KeyboardEvent && event.key !== 'Enter') {
      return;
    }

    if (!(event.target instanceof HTMLElement) || typeof this.dataset.sectionId !== 'string') {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.findIndex((c) => c.toLowerCase() === discountCode.toLowerCase());
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        let errorMsg = 'Failed to remove discount. Please try again.';
        try {
          const errData = await response.json();
          if (errData.description) errorMsg = errData.description;
          else if (errData.message) errorMsg = errData.message;
        } catch (_) {}
        throw new Error(errorMsg);
      }

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      if (data.sections?.[this.dataset.sectionId]) {
        morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId], {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    if (type === 'shipping') {
      cartDiscountError.classList.add('cart-discount__error--shipping-notice');
      cartDiscountError.classList.remove('cart-discount__error--invalid');
    } else {
      cartDiscountError.classList.add('cart-discount__error--invalid');
      cartDiscountError.classList.remove('cart-discount__error--shipping-notice');
      this.dispatchEvent(
        new CartErrorEvent({
          error: 'Invalid discount code',
          code: 'VALIDATION_CUSTOM',
        })
      );
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
